import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  FileCheck,
  Clock,
  AlertTriangle,
  Plus,
  Search,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Building2,
  CreditCard,
  Calendar,
  Send,
  FileText,
  Users,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { NEU } from '../neuStyles';

const API = window.zignagenixAPI;

const InsurancePage = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [verificationView, setVerificationView] = useState(null);
  const [detailView, setDetailView] = useState(null);

  // Dashboard state
  const [summary, setSummary] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchDays, setBatchDays] = useState(7);

  // Verifications state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [providers, setProviders] = useState([]);

  // New verification form
  const [verifyForm, setVerifyForm] = useState({
    patient_id: '',
    insurance_provider: '',
    member_id: '',
    group_number: '',
    service_type: 'medical',
  });

  // Manual verification form
  const [manualForm, setManualForm] = useState({
    is_eligible: true,
    copay: '',
    deductible: '',
    deductible_met: '',
    coinsurance_pct: '',
    out_of_pocket_max: '',
    oop_met: '',
    prior_auth_required: false,
    coverage_details: '',
    notes: '',
  });

  // Prior auth state
  const [priorAuths, setPriorAuths] = useState([]);
  const [priorAuthFilters, setPriorAuthFilters] = useState({ status: 'all', urgency: 'all' });
  const [showPriorAuthForm, setShowPriorAuthForm] = useState(false);
  const [priorAuthForm, setPriorAuthForm] = useState({
    patient_id: '',
    insurance_verification_id: '',
    procedure_codes: '',
    diagnosis_codes: '',
    urgency: 'routine',
    clinical_notes: '',
  });
  const [editingPriorAuth, setEditingPriorAuth] = useState(null);
  const [priorAuthUpdateForm, setPriorAuthUpdateForm] = useState({
    status: '',
    auth_number: '',
    approval_date: '',
    expiry_date: '',
    denied_reason: '',
  });

  // Providers state
  const [showNewProvider, setShowNewProvider] = useState(false);
  const [newProviderForm, setNewProviderForm] = useState({
    payer_id: '',
    name: '',
    phone: '',
    website: '',
    portal_url: '',
    notes: '',
  });

  // Load dashboard
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadSummary();
    }
  }, [activeTab]);

  // Load verifications tab
  useEffect(() => {
    if (activeTab === 'verifications') {
      loadProviders();
    }
  }, [activeTab]);

  // Load prior auth tab
  useEffect(() => {
    if (activeTab === 'prior-auth') {
      loadPriorAuths();
    }
  }, [activeTab, priorAuthFilters]);

  // Load providers tab
  useEffect(() => {
    if (activeTab === 'providers') {
      loadProviders();
    }
  }, [activeTab]);

  const loadSummary = async () => {
    try {
      const data = await API.getInsuranceSummary();
      setSummary(data);
    } catch (error) {
      console.error('Failed to load insurance summary:', error);
    }
  };

  const loadProviders = async () => {
    try {
      const data = await API.getInsuranceProviders();
      setProviders(data);
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  const loadPriorAuths = async () => {
    try {
      const filters = {};
      if (priorAuthFilters.status !== 'all') filters.status = priorAuthFilters.status;
      if (priorAuthFilters.urgency !== 'all') filters.urgency = priorAuthFilters.urgency;
      const data = await API.getPriorAuths(filters);
      setPriorAuths(data);
    } catch (error) {
      console.error('Failed to load prior auths:', error);
    }
  };

  const searchPatients = async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const results = await API.searchPatients(term);
      setSearchResults(results);
    } catch (error) {
      console.error('Failed to search patients:', error);
    }
  };

  const handlePatientSelect = async (patient) => {
    setSelectedPatient(patient);
    setVerifyForm({ ...verifyForm, patient_id: patient.id });
    try {
      const history = await API.getInsuranceHistory(patient.id);
      setVerificationHistory(history);
    } catch (error) {
      console.error('Failed to load verification history:', error);
    }
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleVerifyInsurance = async (e) => {
    e.preventDefault();
    try {
      const result = await API.verifyInsurance(verifyForm);
      if (result.status === 'verified' || result.status === 'pending_manual') {
        if (selectedPatient) {
          const history = await API.getInsuranceHistory(selectedPatient.id);
          setVerificationHistory(history);
        }
        setVerifyForm({
          patient_id: verifyForm.patient_id,
          insurance_provider: '',
          member_id: '',
          group_number: '',
          service_type: 'medical',
        });
      }
      if (result.status === 'pending_manual') {
        setDetailView(result.id);
      }
    } catch (error) {
      console.error('Failed to verify insurance:', error);
    }
  };

  const handleManualVerify = async (e, verificationId) => {
    e.preventDefault();
    try {
      await API.verifyInsuranceManual(verificationId, manualForm);
      if (selectedPatient) {
        const history = await API.getInsuranceHistory(selectedPatient.id);
        setVerificationHistory(history);
      }
      setDetailView(null);
      setManualForm({
        is_eligible: true,
        copay: '',
        deductible: '',
        deductible_met: '',
        coinsurance_pct: '',
        out_of_pocket_max: '',
        oop_met: '',
        prior_auth_required: false,
        coverage_details: '',
        notes: '',
      });
    } catch (error) {
      console.error('Failed to save manual verification:', error);
    }
  };

  const handleBatchVerify = async () => {
    setBatchLoading(true);
    try {
      await API.batchVerifyInsurance(batchDays);
      await loadSummary();
    } catch (error) {
      console.error('Failed to batch verify:', error);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleCreatePriorAuth = async (e) => {
    e.preventDefault();
    try {
      await API.createPriorAuth(priorAuthForm);
      await loadPriorAuths();
      setShowPriorAuthForm(false);
      setPriorAuthForm({
        patient_id: '',
        insurance_verification_id: '',
        procedure_codes: '',
        diagnosis_codes: '',
        urgency: 'routine',
        clinical_notes: '',
      });
    } catch (error) {
      console.error('Failed to create prior auth:', error);
    }
  };

  const handleUpdatePriorAuth = async (e, priorAuthId) => {
    e.preventDefault();
    try {
      await API.updatePriorAuth(priorAuthId, priorAuthUpdateForm);
      await loadPriorAuths();
      setEditingPriorAuth(null);
      setPriorAuthUpdateForm({
        status: '',
        auth_number: '',
        approval_date: '',
        expiry_date: '',
        denied_reason: '',
      });
    } catch (error) {
      console.error('Failed to update prior auth:', error);
    }
  };

  const handleCreateProvider = async (e) => {
    e.preventDefault();
    try {
      await API.createInsuranceProvider(newProviderForm);
      await loadProviders();
      setShowNewProvider(false);
      setNewProviderForm({
        payer_id: '',
        name: '',
        phone: '',
        website: '',
        portal_url: '',
        notes: '',
      });
    } catch (error) {
      console.error('Failed to create provider:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return '#10b981';
      case 'pending_manual':
        return '#f59e0b';
      case 'denied':
        return '#ef4444';
      case 'expired':
        return '#9ca3af';
      default:
        return '#6b7280';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      verified: { bg: '#d1fae5', text: '#065f46' },
      pending_manual: { bg: '#fef3c7', text: '#92400e' },
      denied: { bg: '#fee2e2', text: '#991b1b' },
      expired: { bg: '#f3f4f6', text: '#374151' },
      draft: { bg: '#f3f4f6', text: '#374151' },
      submitted: { bg: '#dbeafe', text: '#0c2340' },
      approved: { bg: '#d1fae5', text: '#065f46' },
    };
    const color = colors[status] || colors.draft;
    return { backgroundColor: color.bg, color: color.text };
  };

  // Dashboard Tab
  const renderDashboard = () => (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '24px', color: '#1f2937' }}>
        Insurance Verification Dashboard
      </h1>

      {summary && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <div style={{
              ...NEU.cardSm,
              padding: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <ShieldCheck size={24} style={{ color: '#2E8B98' }} />
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Verifications This Week</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
                {summary.verifications_this_week}
              </div>
            </div>

            <div style={{
              ...NEU.cardSm,
              padding: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <FileCheck size={24} style={{ color: '#2E8B98' }} />
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Approval Rate</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
                {summary.approval_rate_pct}%
              </div>
            </div>

            <div style={{
              ...NEU.cardSm,
              padding: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <Clock size={24} style={{ color: '#f59e0b' }} />
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Pending Prior Auths</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
                {typeof summary.pending_prior_auths === 'object' && summary.pending_prior_auths !== null
                  ? Object.values(summary.pending_prior_auths).reduce((a, b) => a + b, 0)
                  : summary.pending_prior_auths}
              </div>
            </div>

            <div style={{
              ...NEU.cardSm,
              padding: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Expiring in 30 Days</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
                {summary.expiring_authorizations_30days}
              </div>
            </div>
          </div>

          <div style={{
            ...NEU.card,
            padding: '24px',
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
              Batch Verify Upcoming Appointments
            </h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
                  Days Ahead
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={batchDays}
                  onChange={(e) => setBatchDays(parseInt(e.target.value))}
                  style={{
                    ...NEU.input,
                    width: '100px',
                  }}
                />
              </div>
              <button
                onClick={handleBatchVerify}
                disabled={batchLoading}
                style={{
                  ...NEU.btnPrimary,
                  padding: '8px 16px',
                  opacity: batchLoading ? 0.6 : 1,
                  cursor: batchLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                }}
              >
                <RefreshCw size={16} />
                {batchLoading ? 'Verifying...' : 'Batch Verify'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Verifications Tab
  const renderVerifications = () => (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '24px', color: '#1f2937' }}>
        Insurance Verifications
      </h1>

      {!selectedPatient ? (
        <div style={{
          ...NEU.card,
          padding: '24px',
          marginBottom: '24px',
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#1f2937' }}>
            Search Patient
          </h3>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                searchPatients(e.target.value);
              }}
              style={{
                ...NEU.input,
                width: '100%',
                paddingLeft: '36px',
              }}
            />
            <Search size={18} style={{ position: 'absolute', left: '10px', top: '11px', color: '#9ca3af' }} />
            {searchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                ...NEU.card,
                marginTop: '4px',
                zIndex: 10,
              }}>
                {searchResults.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => handlePatientSelect(patient)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      textAlign: 'left',
                      border: 'none',
                      background: NEU.bg,
                      cursor: 'pointer',
                      borderBottom: `1px solid rgba(186,199,216,0.3)`,
                      fontSize: '14px',
                      color: '#1f2937',
                    }}
                  >
                    {patient.name} ({patient.id})
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => {
                setSelectedPatient(null);
                setVerificationHistory([]);
                setVerifyForm({
                  patient_id: '',
                  insurance_provider: '',
                  member_id: '',
                  group_number: '',
                  service_type: 'medical',
                });
              }}
              style={{
                ...NEU.btnGhost,
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
              }}
            >
              <ArrowLeft size={16} />
              Back to Search
            </button>
          </div>

          <h2 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>
            {selectedPatient.name}
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            <div style={{
              ...NEU.card,
              padding: '24px',
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                New Verification
              </h3>
              <form onSubmit={handleVerifyInsurance} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                    Insurance Provider
                  </label>
                  <select
                    value={verifyForm.insurance_provider}
                    onChange={(e) => setVerifyForm({ ...verifyForm, insurance_provider: e.target.value })}
                    style={{
                      ...NEU.input,
                      width: '100%',
                    }}
                    required
                  >
                    <option value="">Select provider</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                    Member ID
                  </label>
                  <input
                    type="text"
                    value={verifyForm.member_id}
                    onChange={(e) => setVerifyForm({ ...verifyForm, member_id: e.target.value })}
                    style={{
                      ...NEU.input,
                      width: '100%',
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                    Group Number
                  </label>
                  <input
                    type="text"
                    value={verifyForm.group_number}
                    onChange={(e) => setVerifyForm({ ...verifyForm, group_number: e.target.value })}
                    style={{
                      ...NEU.input,
                      width: '100%',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                    Service Type
                  </label>
                  <select
                    value={verifyForm.service_type}
                    onChange={(e) => setVerifyForm({ ...verifyForm, service_type: e.target.value })}
                    style={{
                      ...NEU.input,
                      width: '100%',
                    }}
                  >
                    <option value="medical">Medical</option>
                    <option value="dental">Dental</option>
                    <option value="vision">Vision</option>
                    <option value="behavioral">Behavioral Health</option>
                  </select>
                </div>

                <button
                  type="submit"
                  style={{
                    ...NEU.btnPrimary,
                    padding: '10px 16px',
                    marginTop: '8px',
                  }}
                >
                  Verify Insurance
                </button>
              </form>
            </div>

            <div style={{
              ...NEU.card,
              padding: '24px',
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                Verification History
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {verificationHistory.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No verifications found</p>
                ) : (
                  verificationHistory.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setDetailView(v.id)}
                      style={{
                        ...NEU.cardSm,
                        padding: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = NEU.shadow.md}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = NEU.shadow.xs}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px' }}>
                            {v.insurance_provider}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '13px' }}>
                            {v.member_id}
                          </div>
                        </div>
                        <span style={{
                          ...getStatusBadge(v.status),
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                        }}>
                          {v.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {detailView && (
        <div style={{
          ...NEU.card,
          padding: '24px',
        }}>
          <button
            onClick={() => setDetailView(null)}
            style={{
              ...NEU.btnGhost,
              padding: '6px 12px',
              fontSize: '13px',
              marginBottom: '16px',
            }}
          >
            <ArrowLeft size={14} style={{ display: 'inline', marginRight: '4px' }} />
            Back
          </button>

          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>
            Verification Details
          </h3>

          {verificationHistory.find((v) => v.id === detailView)?.status === 'pending_manual' ? (
            <form onSubmit={(e) => handleManualVerify(e, detailView)} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Eligible?
                </label>
                <select
                  value={manualForm.is_eligible}
                  onChange={(e) => setManualForm({ ...manualForm, is_eligible: e.target.value === 'true' })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Copay ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={manualForm.copay}
                  onChange={(e) => setManualForm({ ...manualForm, copay: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Deductible ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={manualForm.deductible}
                  onChange={(e) => setManualForm({ ...manualForm, deductible: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Deductible Met ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={manualForm.deductible_met}
                  onChange={(e) => setManualForm({ ...manualForm, deductible_met: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Coinsurance (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={manualForm.coinsurance_pct}
                  onChange={(e) => setManualForm({ ...manualForm, coinsurance_pct: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Out-of-Pocket Max ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={manualForm.out_of_pocket_max}
                  onChange={(e) => setManualForm({ ...manualForm, out_of_pocket_max: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  OOP Met ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={manualForm.oop_met}
                  onChange={(e) => setManualForm({ ...manualForm, oop_met: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Prior Auth Required?
                </label>
                <select
                  value={manualForm.prior_auth_required}
                  onChange={(e) => setManualForm({ ...manualForm, prior_auth_required: e.target.value === 'true' })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Coverage Details
                </label>
                <textarea
                  value={manualForm.coverage_details}
                  onChange={(e) => setManualForm({ ...manualForm, coverage_details: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                    minHeight: '80px',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Notes
                </label>
                <textarea
                  value={manualForm.notes}
                  onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                  style={{
                    ...NEU.input,
                    width: '100%',
                    minHeight: '80px',
                    resize: 'vertical',
                  }}
                />
              </div>

              <button
                type="submit"
                style={{
                  gridColumn: '1 / -1',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  marginTop: '8px',
                }}
              >
                Save Manual Verification
              </button>
            </form>
          ) : (
            <VerificationDetailCard verification={verificationHistory.find((v) => v.id === detailView)} />
          )}
        </div>
      )}
    </div>
  );

  // Prior Auth Tab
  const renderPriorAuth = () => (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>
          Prior Authorizations
        </h1>
        <button
          onClick={() => setShowPriorAuthForm(true)}
          style={{
            ...NEU.btnPrimary,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Plus size={16} />
          New Prior Auth
        </button>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
            Status
          </label>
          <select
            value={priorAuthFilters.status}
            onChange={(e) => setPriorAuthFilters({ ...priorAuthFilters, status: e.target.value })}
            style={{
              ...NEU.input,
            }}
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
            Urgency
          </label>
          <select
            value={priorAuthFilters.urgency}
            onChange={(e) => setPriorAuthFilters({ ...priorAuthFilters, urgency: e.target.value })}
            style={{
              ...NEU.input,
            }}
          >
            <option value="all">All Urgencies</option>
            <option value="routine">Routine</option>
            <option value="urgent">Urgent</option>
            <option value="emergent">Emergent</option>
          </select>
        </div>
      </div>

      {showPriorAuthForm && (
        <div style={{
          ...NEU.card,
          padding: '24px',
          marginBottom: '24px',
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
            New Prior Authorization
          </h3>
          <form onSubmit={handleCreatePriorAuth} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Patient ID
              </label>
              <input
                type="text"
                value={priorAuthForm.patient_id}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, patient_id: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Insurance Verification ID
              </label>
              <input
                type="text"
                value={priorAuthForm.insurance_verification_id}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, insurance_verification_id: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Procedure Codes (comma-separated)
              </label>
              <input
                type="text"
                value={priorAuthForm.procedure_codes}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, procedure_codes: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Diagnosis Codes (comma-separated)
              </label>
              <input
                type="text"
                value={priorAuthForm.diagnosis_codes}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, diagnosis_codes: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Urgency
              </label>
              <select
                value={priorAuthForm.urgency}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, urgency: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
              >
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="emergent">Emergent</option>
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Clinical Notes
              </label>
              <textarea
                value={priorAuthForm.clinical_notes}
                onChange={(e) => setPriorAuthForm({ ...priorAuthForm, clinical_notes: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                    minHeight: '100px',
                    resize: 'vertical',
                  }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                style={{
                  ...NEU.btnPrimary,
                  padding: '10px 16px',
                }}
              >
                Create Prior Auth
              </button>
              <button
                type="button"
                onClick={() => setShowPriorAuthForm(false)}
                style={{
                  ...NEU.btnGhost,
                  padding: '10px 16px',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{
        ...NEU.card,
        padding: '24px',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {priorAuths.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '14px' }}>No prior authorizations found</p>
          ) : (
            priorAuths.map((pa) => (
              <div
                key={pa.id}
                style={{
                  ...NEU.cardSm,
                  padding: '16px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                      {pa.patient_id}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>
                      Procedures: {typeof pa.procedure_codes === 'object' ? JSON.stringify(pa.procedure_codes) : pa.procedure_codes} | Urgency: {pa.urgency}
                    </div>
                  </div>
                  <span style={{
                    ...getStatusBadge(pa.status),
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}>
                    {pa.status}
                  </span>
                </div>

                {pa.status !== 'draft' && pa.status !== 'submitted' && !editingPriorAuth === pa.id && (
                  <button
                    onClick={() => {
                      setEditingPriorAuth(pa.id);
                      setPriorAuthUpdateForm({
                        status: pa.status,
                        auth_number: pa.auth_number || '',
                        approval_date: pa.approval_date || '',
                        expiry_date: pa.expiry_date || '',
                        denied_reason: pa.denied_reason || '',
                      });
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#e0f2fe',
                      border: '1px solid #2E8B98',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#2E8B98',
                      fontWeight: '500',
                    }}
                  >
                    Edit Status
                  </button>
                )}

                {editingPriorAuth === pa.id && (
                  <form onSubmit={(e) => handleUpdatePriorAuth(e, pa.id)} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '3px', color: '#374151' }}>
                        Status
                      </label>
                      <select
                        value={priorAuthUpdateForm.status}
                        onChange={(e) => setPriorAuthUpdateForm({ ...priorAuthUpdateForm, status: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                        }}
                      >
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="denied">Denied</option>
                        <option value="expired">Expired</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '3px', color: '#374151' }}>
                        Auth Number
                      </label>
                      <input
                        type="text"
                        value={priorAuthUpdateForm.auth_number}
                        onChange={(e) => setPriorAuthUpdateForm({ ...priorAuthUpdateForm, auth_number: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '3px', color: '#374151' }}>
                        Approval Date
                      </label>
                      <input
                        type="date"
                        value={priorAuthUpdateForm.approval_date}
                        onChange={(e) => setPriorAuthUpdateForm({ ...priorAuthUpdateForm, approval_date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '3px', color: '#374151' }}>
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        value={priorAuthUpdateForm.expiry_date}
                        onChange={(e) => setPriorAuthUpdateForm({ ...priorAuthUpdateForm, expiry_date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                        }}
                      />
                    </div>

                    {priorAuthUpdateForm.status === 'denied' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '3px', color: '#374151' }}>
                          Denial Reason
                        </label>
                        <textarea
                          value={priorAuthUpdateForm.denied_reason}
                          onChange={(e) => setPriorAuthUpdateForm({ ...priorAuthUpdateForm, denied_reason: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            border: 'none',
                            fontSize: '12px',
                            fontFamily: 'inherit',
                            minHeight: '60px',
                            resize: 'vertical',
                          }}
                        />
                      </div>
                    )}

                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
                      <button
                        type="submit"
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '12px',
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingPriorAuth(null)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          ...NEU.btnGhost,
                          fontSize: '12px',
                          color: '#374151',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  // Providers Tab
  const renderProviders = () => (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>
          Insurance Providers
        </h1>
        <button
          onClick={() => setShowNewProvider(true)}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            background: 'linear-gradient(145deg, #2E8B98, #007a96)',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Plus size={16} />
          Add Provider
        </button>
      </div>

      {showNewProvider && (
        <div style={{
          ...NEU.card,
          padding: '24px',
          marginBottom: '24px',
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
            New Insurance Provider
          </h3>
          <form onSubmit={handleCreateProvider} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Payer ID
              </label>
              <input
                type="text"
                value={newProviderForm.payer_id}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, payer_id: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Name
              </label>
              <input
                type="text"
                value={newProviderForm.name}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, name: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Phone
              </label>
              <input
                type="tel"
                value={newProviderForm.phone}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, phone: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Website
              </label>
              <input
                type="url"
                value={newProviderForm.website}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, website: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Portal URL
              </label>
              <input
                type="url"
                value={newProviderForm.portal_url}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, portal_url: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                  }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                Notes
              </label>
              <textarea
                value={newProviderForm.notes}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, notes: e.target.value })}
                style={{
                    ...NEU.input,
                    width: '100%',
                    minHeight: '80px',
                    resize: 'vertical',
                  }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                style={{
                  ...NEU.btnPrimary,
                  padding: '10px 16px',
                }}
              >
                Create Provider
              </button>
              <button
                type="button"
                onClick={() => setShowNewProvider(false)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  ...NEU.btnGhost,
                  color: '#374151',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
        {providers.map((provider) => (
          <div
            key={provider.id}
            style={{
              ...NEU.cardSm,
              padding: '20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              <Building2 size={24} style={{ color: '#2E8B98' }} />
              <div style={{ flex: 1 }}>
                <h3 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                  {provider.name}
                </h3>
                <p style={{ color: '#6b7280', fontSize: '13px' }}>
                  ID: {provider.payer_id}
                </p>
              </div>
            </div>

            {provider.phone && (
              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
                <span style={{ fontWeight: '500' }}>Phone:</span> {provider.phone}
              </div>
            )}

            {provider.website && (
              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
                <span style={{ fontWeight: '500' }}>Website:</span> {provider.website}
              </div>
            )}

            {provider.portal_url && (
              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
                <span style={{ fontWeight: '500' }}>Portal:</span> {provider.portal_url}
              </div>
            )}

            {provider.notes && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb', fontSize: '13px', color: '#6b7280' }}>
                {provider.notes}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ background: NEU.bg, minHeight: '100vh' }}>
      <div style={{
        borderBottom: 'none',
        background: NEU.bg,
        boxShadow: NEU.shadow.sm,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          gap: '24px',
          /*
           * ⚠ ADDED WITH THE LONGER LABEL — Card P114 (F3). "Prior Auth"
           * became "Prior Authorizations", so this row is ~9 characters wider
           * and a `display:flex` with no wrap would push the last tab out of
           * the 1200px container on a narrow laptop or a tablet. `wrap` is the
           * house pattern for a nav row (src/pages/crm/GroupedTabNav.js:128)
           * rather than a horizontal scroll, which hides tabs behind a gesture
           * nobody discovers.
           */
          flexWrap: 'wrap',
          rowGap: '4px',
          borderBottom: 'none',
        }}>
          {[
            { id: 'dashboard', label: 'Dashboard', icon: ShieldCheck },
            { id: 'verifications', label: 'Verifications', icon: FileCheck },
            /*
             * ⛔ "Prior Auth" read as a truncation and staff reported it as one
             * (Luisa, 23 Oct). It never was: the label was authored short while
             * the panel it opens says "Prior Authorizations" in full at :901
             * and :965. The three now agree.
             */
            { id: 'prior-auth', label: 'Prior Authorizations', icon: Clock },
            { id: 'providers', label: 'Providers', icon: Building2 },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                padding: '12px 0',
                borderBottom: activeTab === id ? '3px solid #2E8B98' : 'none',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: activeTab === id ? '#2E8B98' : '#6b7280',
                fontWeight: activeTab === id ? '600' : '500',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: NEU.bg }}>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'verifications' && renderVerifications()}
        {activeTab === 'prior-auth' && renderPriorAuth()}
        {activeTab === 'providers' && renderProviders()}
      </div>
    </div>
  );
};

const VerificationDetailCard = ({ verification }) => {
  if (!verification) return null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      <div style={{
        backgroundColor: '#f0f9ff',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #bfdbfe',
      }}>
        <div style={{ color: '#0c2340', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
          Eligibility Status
        </div>
        <div style={{ fontSize: '16px', fontWeight: '600', color: verification.is_eligible ? '#065f46' : '#991b1b' }}>
          {verification.is_eligible ? 'Eligible' : 'Not Eligible'}
        </div>
      </div>

      <div style={{
        backgroundColor: '#fef3c7',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #fcd34d',
      }}>
        <div style={{ color: '#92400e', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
          Copay
        </div>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
          ${verification.copay}
        </div>
      </div>

      <div>
        <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
          Deductible
        </div>
        <div style={{ fontSize: '14px', color: '#1f2937' }}>
          ${verification.deductible}
        </div>
        <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
          Met: ${verification.deductible_met}
        </div>
      </div>

      <div>
        <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
          Coinsurance
        </div>
        <div style={{ fontSize: '14px', color: '#1f2937' }}>
          {verification.coinsurance_pct}%
        </div>
      </div>

      <div>
        <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
          Out-of-Pocket Max
        </div>
        <div style={{ fontSize: '14px', color: '#1f2937' }}>
          ${verification.out_of_pocket_max}
        </div>
        <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
          Met: ${verification.oop_met}
        </div>
      </div>

      <div>
        <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
          Prior Auth Required
        </div>
        <div style={{ fontSize: '14px', color: '#1f2937' }}>
          {verification.prior_auth_required ? 'Yes' : 'No'}
        </div>
      </div>

      {verification.coverage_details && (
        <div style={{ gridColumn: '1 / -1', ...NEU.inset, padding: '12px' }}>
          <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500', marginBottom: '8px' }}>
            Coverage Details
          </div>
          <div style={{ fontSize: '13px', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
            {typeof verification.coverage_details === 'object' ? JSON.stringify(verification.coverage_details, null, 2) : verification.coverage_details}
          </div>
        </div>
      )}

      {verification.notes && (
        <div style={{ gridColumn: '1 / -1', ...NEU.inset, padding: '12px' }}>
          <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500', marginBottom: '8px' }}>
            Notes
          </div>
          <div style={{ fontSize: '13px', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
            {verification.notes}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsurancePage;
